// Compact card showing the most recent aXcelerate sync run plus the
// known domains. Lives on the Overview page so the platform's
// "is the data fresh?" answer is one glance away.
//
// The "Sync now" button POSTs to /api/connectors/axcelerate/sync,
// which proxies through the worker-to-worker service binding to the
// collector worker's fetch() handler. Useful for the first run after
// secrets are set or when the field-name guesses in the sync modules
// need iterating.

import { useCallback, useEffect, useState } from 'react';
import { api, type AxcelerateDomain, type AxcelerateSyncRun, type SyncRunStatus } from '../lib/api';

const STATUS_LABEL: Record<NonNullable<SyncRunStatus>, string> = {
  success: 'OK',
  partial: 'Partial',
  failed: 'Failed',
  skipped: 'Not configured',
};

const STATUS_TONE: Record<NonNullable<SyncRunStatus>, string> = {
  success: 'tone-good',
  partial: 'tone-warn',
  failed: 'tone-bad',
  skipped: 'tone-muted',
};

export function ConnectorStatus() {
  const [latest, setLatest] = useState<AxcelerateSyncRun | null | undefined>(undefined);
  const [domains, setDomains] = useState<AxcelerateDomain[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [runs, doms] = await Promise.all([
        api.connectors.axcelerate.runs(),
        api.connectors.axcelerate.domains(),
      ]);
      setLatest(runs.runs[0] ?? null);
      setDomains(doms.domains);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const triggerSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      await api.connectors.axcelerate.sync();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="connector-card">
      <header>
        <h2>aXcelerate connector</h2>
        <button
          className="secondary"
          onClick={triggerSync}
          disabled={syncing}
          aria-label="Trigger a sync now"
        >
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      </header>
      {error && <p className="error">{error}</p>}
      {latest === undefined && !error && <p className="muted">Checking…</p>}
      {latest === null && (
        <p className="muted">
          No sync runs recorded yet. Trigger one above or wait for the daily cron at 02:00 UTC.
        </p>
      )}
      {latest && <RunSummary run={latest} />}
      {domains.length > 0 && <DomainList domains={domains} />}
      <style>{styles}</style>
    </div>
  );
}

function RunSummary({ run }: { run: AxcelerateSyncRun }) {
  const status = run.status;
  const tone = status ? STATUS_TONE[status] : 'tone-muted';
  const label = status ? STATUS_LABEL[status] : 'Running';

  return (
    <dl className="kv">
      <dt>Latest run</dt>
      <dd>
        <span className={`pill ${tone}`}>{label}</span>{' '}
        <span className="muted">({run.trigger})</span>
      </dd>
      <dt>Started</dt>
      <dd>{new Date(run.startedAt).toLocaleString()}</dd>
      {run.finishedAt && (
        <>
          <dt>Finished</dt>
          <dd>{new Date(run.finishedAt).toLocaleString()}</dd>
        </>
      )}
      {run.recordsSynced !== null && (
        <>
          <dt>Records</dt>
          <dd>{run.recordsSynced.toLocaleString('en-AU')}</dd>
        </>
      )}
      {run.error && (
        <>
          <dt>Error</dt>
          <dd className="error-text">{run.error}</dd>
        </>
      )}
    </dl>
  );
}

function DomainList({ domains }: { domains: AxcelerateDomain[] }) {
  return (
    <details className="domains">
      <summary>{domains.length} domain{domains.length === 1 ? '' : 's'}</summary>
      <ul>
        {domains.map((d) => (
          <li key={d.domainId}>
            <span>{d.domainName}</span>
            {d.isHeadOffice && <span className="badge">primary</span>}
            {!d.isEnabled && <span className="badge muted-badge">not enabled</span>}
          </li>
        ))}
      </ul>
    </details>
  );
}

const styles = `
.connector-card {
  background: var(--ac-white);
  border: 1px solid var(--ac-grey-200);
  border-radius: var(--ac-radius);
  padding: 16px 20px;
  box-shadow: var(--ac-shadow-sm);
}
.connector-card header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px; }
.connector-card h2 { margin: 0; font-size: 16px; color: var(--ac-grey-700); }
.kv { display: grid; grid-template-columns: max-content 1fr; gap: 6px 16px; margin: 0 0 8px; }
.kv dt { color: var(--ac-grey-500); }
.kv dd { margin: 0; font-variant-numeric: tabular-nums; }
.pill {
  display: inline-block; padding: 2px 8px; border-radius: 999px;
  font-size: 12px; font-weight: 500;
}
.tone-good { background: rgba(0, 141, 210, 0.12); color: var(--ac-blue-dark); }
.tone-warn { background: #fff3cd; color: #7a5901; }
.tone-bad { background: #fae3e3; color: #7c1f1f; }
.tone-muted { background: var(--ac-grey-100); color: var(--ac-grey-700); }
.muted { color: var(--ac-grey-500); }
.error-text { color: #7c1f1f; font-size: 13px; word-break: break-word; }
.error { color: #b00020; background: #fae3e3; padding: 8px 12px; border-radius: var(--ac-radius); }

.domains { margin-top: 12px; }
.domains summary { color: var(--ac-grey-700); cursor: pointer; font-size: 14px; }
.domains ul { list-style: none; padding: 8px 0 0; margin: 0; }
.domains li { display: flex; align-items: center; gap: 8px; padding: 4px 0; font-size: 14px; }
.badge { font-size: 11px; padding: 1px 6px; border-radius: 999px; background: rgba(0, 141, 210, 0.12); color: var(--ac-blue-dark); }
.badge.muted-badge { background: var(--ac-grey-100); color: var(--ac-grey-500); }
`;
